import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import ProductImageGallery from '@/components/admin/ProductImageGallery';
import { SimpleGroupBuyFields } from '@/components/admin/SimpleGroupBuyFields';
import { LiquidationSaleFields } from '@/components/admin/LiquidationSaleFields';
import { toast } from 'sonner';
import { validateProductData, sanitizeInput, sanitizeHtml, sanitizeNumericInput } from "@/lib/security";
import { createSlugFromHebrew } from '@/lib/slugUtils';
import { ArrowRight, Save } from 'lucide-react';
import { SecurityHeaders } from '@/components/SecurityHeaders';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import { AdminSidebar } from '@/components/admin/AdminSidebar';
import SupplierQuickCreate from '@/components/admin/SupplierQuickCreate';
import { useAdminStats } from '@/hooks/useAdminStats';

const AdminProductCreate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const duplicateProduct = (location.state as any)?.duplicateProduct;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    sku: '',
    price_per_unit: 0,
    cost_price: null as number | null,
    original_price: null as number | null,
    measurement_unit: 'unit',
    total_inventory: 0,
    min_quantity: 1,
    max_quantity: null as number | null,
    step_quantity: 1,
    supplier_name: '',
    is_active: true,
    images: [] as string[],
    main_image_index: 0,
    category_id: '',
    subcategory_id: '',
    sale_type: 'immediate',
    sale_start: null as string | null,
    sale_end: null as string | null,
    delivery_option: 'both',
    supplier_id: null as string | null,
    package_coverage_per_unit: undefined as number | undefined,
    grade: 'B' as 'A' | 'B' | 'C' | 'D',
    group_buy_enabled: false,
    group_buy_target_quantity: null as number | null,
    group_buy_current_quantity: 0,
    group_buy_end_date: null as string | null,
    group_buy_locked_price: null as number | null,
    group_buy_limit_to_target: false,
    // Liquidation fields
    liquidation_threshold_percent: null as number | null,
    liquidation_delivery_days: null as number | null,
    // Dimension fields
    dimension_width: null as number | null,
    dimension_height: null as number | null,
    dimension_depth: null as number | null,
    dimension_unit: 'cm',
    dimension_width_unit: 'cm',
    dimension_height_unit: 'cm',
    dimension_depth_unit: 'mm',
    // Additional info
    country_of_origin: '',
    material: null as string | null,
    finish: null as string | null,
  });
  const [categories, setCategories] = useState<any[]>([]);
  const [subcategories, setSubcategories] = useState<any[]>([]);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Handle duplicate product data
  useEffect(() => {
    if (duplicateProduct) {
      try {
        console.log('Duplicate product data:', duplicateProduct);
        setFormData({
          name: duplicateProduct.name || '',
          description: duplicateProduct.description || '',
          sku: duplicateProduct.sku || '',
          price_per_unit: duplicateProduct.price_per_unit || 0,
          cost_price: duplicateProduct.cost_price || null,
          original_price: duplicateProduct.original_price || null,
          measurement_unit: duplicateProduct.measurement_unit || 'unit',
          total_inventory: duplicateProduct.total_inventory || 0,
          min_quantity: duplicateProduct.min_quantity || 1,
          max_quantity: duplicateProduct.max_quantity || null,
          step_quantity: duplicateProduct.step_quantity || 1,
          supplier_name: duplicateProduct.supplier_name || '',
          is_active: duplicateProduct.is_active !== undefined ? duplicateProduct.is_active : true,
          images: Array.isArray(duplicateProduct.images) ? duplicateProduct.images : [],
          main_image_index: duplicateProduct.main_image_index ?? 0,
          category_id: duplicateProduct.category_id || '',
          subcategory_id: duplicateProduct.subcategory_id || '',
          sale_type: duplicateProduct.sale_type || 'immediate',
          sale_start: duplicateProduct.sale_start || null,
          sale_end: duplicateProduct.sale_end || null,
          delivery_option: duplicateProduct.delivery_option || 'both',
          supplier_id: duplicateProduct.supplier_id || null,
          package_coverage_per_unit: duplicateProduct.package_coverage_per_unit || undefined,
          grade: duplicateProduct.grade || 'B',
          group_buy_enabled: duplicateProduct.group_buy_enabled || false,
          group_buy_target_quantity: duplicateProduct.group_buy_target_quantity || null,
          group_buy_current_quantity: duplicateProduct.group_buy_current_quantity || 0,
          group_buy_end_date: duplicateProduct.group_buy_end_date || null,
          group_buy_locked_price: duplicateProduct.group_buy_locked_price || null,
          group_buy_limit_to_target: duplicateProduct.group_buy_limit_to_target || false,
          // Liquidation fields
          liquidation_threshold_percent: duplicateProduct.liquidation_threshold_percent || null,
          liquidation_delivery_days: duplicateProduct.liquidation_delivery_days || null,
          // Dimension fields
          dimension_width: duplicateProduct.dimension_width || null,
          dimension_height: duplicateProduct.dimension_height || null,
          dimension_depth: duplicateProduct.dimension_depth || null,
          dimension_unit: duplicateProduct.dimension_unit || 'cm',
          // Additional info
          country_of_origin: duplicateProduct.country_of_origin || '',
          material: duplicateProduct.material || null,
          finish: duplicateProduct.finish || null,
        });
        toast.success('נתוני המוצר הועתקו בהצלחה');
      } catch (error) {
        console.error('Error applying duplicate data:', error);
        toast.error('שגיאה בטעינת נתוני המוצר');
      }
    }
  }, [duplicateProduct]);

  useEffect(() => {
    fetchCategories();
    fetchSuppliers();
  }, []);

  useEffect(() => {
    if (formData.category_id) {
      fetchSubcategories(formData.category_id);
    }
  }, [formData.category_id]);

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase.from('categories').select('*').order('name');
      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('שגיאה בטעינת קטגוריות');
    }
  };

  const fetchSubcategories = async (categoryId: string) => {
    try {
      const { data, error } = await supabase
        .from('subcategories')
        .select('*')
        .eq('category_id', categoryId)
        .order('name');
      if (error) throw error;
      setSubcategories(data || []);
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      toast.error('שגיאה בטעינת תתי-קטגוריות');
    }
  };

  const fetchSuppliers = async () => {
    try {
      const { data, error } = await supabase
        .from('suppliers')
        .select('id, name, city')
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      setSuppliers(data || []);

      // Set default supplier to "אקסס מרקט" if no supplier selected
      if (!formData.supplier_id && data && data.length > 0) {
        const defaultSupplier = data.find(s => s.name.includes('אקסס מרקט') || s.name.includes('אקסס'));
        if (defaultSupplier) {
          setFormData(prev => ({ ...prev, supplier_id: defaultSupplier.id }));
        }
      }
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      toast.error('שגיאה בטעינת ספקים');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Enhanced security validation
      const validation = validateProductData(formData);
      if (!validation.isValid) {
        validation.errors.forEach(error => toast.error(error));
        setLoading(false);
        return;
      }

      // Validate original price
      if (formData.original_price && formData.original_price <= formData.price_per_unit) {
        toast.error('המחיר המקורי חייב להיות גבוה מהמחיר הנוכחי (אחרי הנחה)');
        setLoading(false);
        return;
      }

      // Sanitize input data
      const sanitizedData = {
        ...formData,
        name: sanitizeInput(formData.name),
        description: sanitizeHtml(formData.description || ''),
        sku: sanitizeInput(formData.sku || ''),
        supplier_sku: sanitizeInput(formData.supplier_sku || ''),
        supplier_name: sanitizeInput(formData.supplier_name),
        price_per_unit: sanitizeNumericInput(formData.price_per_unit || 0),
        cost_price: formData.cost_price ? sanitizeNumericInput(formData.cost_price) : null,
        original_price: formData.original_price ? sanitizeNumericInput(formData.original_price) : null,
        total_inventory: sanitizeNumericInput(formData.total_inventory || 0),
        min_quantity: sanitizeNumericInput(formData.min_quantity || 1),
        max_quantity: formData.max_quantity ? sanitizeNumericInput(formData.max_quantity) : null,
        step_quantity: sanitizeNumericInput(formData.step_quantity || 1),
        // Sanitize new fields
        dimension_width: formData.dimension_width ? sanitizeNumericInput(formData.dimension_width) : null,
        dimension_height: formData.dimension_height ? sanitizeNumericInput(formData.dimension_height) : null,
        dimension_depth: formData.dimension_depth ? sanitizeNumericInput(formData.dimension_depth) : null,
        dimension_width_unit: formData.dimension_width_unit,
        dimension_height_unit: formData.dimension_height_unit,
        dimension_depth_unit: formData.dimension_depth_unit,
        country_of_origin: sanitizeInput(formData.country_of_origin || ''),
        material: sanitizeInput(formData.material || ''),
        finish: sanitizeInput(formData.finish || ''),
        // Liquidation fields
        liquidation_threshold_percent: formData.liquidation_threshold_percent ? sanitizeNumericInput(formData.liquidation_threshold_percent) : null,
        liquidation_delivery_days: formData.liquidation_delivery_days ? sanitizeNumericInput(formData.liquidation_delivery_days) : null,
      };

      // Validation logic
      if (sanitizedData.max_quantity && sanitizedData.min_quantity && sanitizedData.max_quantity < sanitizedData.min_quantity) {
        toast.error(`הכמות המקסימלית (${sanitizedData.max_quantity}) לא יכולה להיות נמוכה מהכמות המינימלית (${sanitizedData.min_quantity})`);
        setLoading(false);
        return;
      }

      // Only check max_quantity against total_inventory for non-group-buy products
      if (!formData.group_buy_enabled && sanitizedData.max_quantity && sanitizedData.total_inventory && sanitizedData.max_quantity > sanitizedData.total_inventory) {
        toast.error(`הכמות המקסימלית להזמנה (${sanitizedData.max_quantity}) לא יכולה לעלות על המלאי הכולל (${sanitizedData.total_inventory})`);
        setLoading(false);
        return;
      }

      if (!sanitizedData.name || !sanitizedData.category_id || !sanitizedData.subcategory_id) {
        toast.error('אנא מלא את כל השדות הנדרשים');
        setLoading(false);
        return;
      }

      // Get supplier city if supplier is selected
      const selectedSupplier = suppliers.find(s => s.id === formData.supplier_id);

      const productData = {
        name: sanitizedData.name,
        description: sanitizedData.description,
        sku: sanitizedData.sku || null,
        supplier_sku: sanitizedData.supplier_sku || null,
        price_per_unit: sanitizedData.price_per_unit,
        cost_price: sanitizedData.cost_price,
        original_price: sanitizedData.original_price,
        measurement_unit: sanitizedData.measurement_unit as any,
        package_coverage_per_unit: sanitizedData.package_coverage_per_unit || null,
        minimum_packages: (sanitizedData as any).minimum_packages || null,
        total_inventory: sanitizedData.total_inventory,
        min_quantity: sanitizedData.min_quantity,
        max_quantity: sanitizedData.max_quantity,
        step_quantity: sanitizedData.step_quantity,
        city: selectedSupplier?.city || (sanitizedData as any).city || 'לא צוין',
        supplier_id: sanitizedData.supplier_id || null,
        supplier_name: sanitizedData.supplier_name || '',
        is_active: sanitizedData.is_active,
        images: sanitizedData.images,
        main_image_index: sanitizedData.main_image_index || 0,
        category_id: sanitizedData.category_id,
        subcategory_id: sanitizedData.subcategory_id,
        sale_type: sanitizedData.sale_type as "immediate" | "liquidation",
        delivery_option: sanitizedData.delivery_option as "both" | "pickup_only" | "delivery_only",
        slug: createSlugFromHebrew(sanitizedData.name),
        grade: sanitizedData.grade || 'B',
        group_buy_enabled: sanitizedData.group_buy_enabled || false,
        group_buy_target_quantity: sanitizedData.group_buy_target_quantity || null,
        group_buy_current_quantity: sanitizedData.group_buy_current_quantity || 0,
        group_buy_end_date: sanitizedData.group_buy_end_date || null,
        group_buy_locked_price: sanitizedData.group_buy_locked_price || null,
        // Liquidation fields
        liquidation_threshold_percent: sanitizedData.liquidation_threshold_percent,
        liquidation_delivery_days: sanitizedData.liquidation_delivery_days,
        // Dimension fields
        dimension_width: sanitizedData.dimension_width || null,
        dimension_height: sanitizedData.dimension_height || null,
        dimension_depth: sanitizedData.dimension_depth || null,
        dimension_unit: sanitizedData.dimension_unit,
        dimension_width_unit: sanitizedData.dimension_width_unit,
        dimension_height_unit: sanitizedData.dimension_height_unit,
        dimension_depth_unit: sanitizedData.dimension_depth_unit,
        // Additional info
        country_of_origin: sanitizedData.country_of_origin || null,
        material: sanitizedData.material || null,
        finish: sanitizedData.finish || null
      };

      const { error } = await supabase.from('products').insert(productData);
      if (error) throw error;

      toast.success('מוצר נוסף בהצלחה');
      navigate('/admin?tab=products');
    } catch (error: any) {
      console.error('Error creating product:', error);
      toast.error(`שגיאה בהוספת מוצר: ${error.message || 'שגיאה לא ידועה'}`);
    } finally {
      setLoading(false);
    }
  };

  const { stats, loading: statsLoading } = useAdminStats();

  return (
    <div className="min-h-screen bg-background" dir="rtl">
      <SecurityHeaders isAdminPage={true} preventIndexing={true} />
      <SidebarProvider>
        <div className="flex min-h-screen w-full">
          <AdminSidebar stats={stats} loading={statsLoading} />

          <SidebarInset className="flex-1 overflow-hidden">
            <div className="h-full px-4 py-8 overflow-y-auto">
              <div className="max-w-4xl mx-auto">
                <div className="flex items-center gap-4 mb-6">
                  <Button
                    variant="outline"
                    onClick={() => navigate('/admin?tab=products')}
                    className="flex items-center gap-2"
                  >
                    <ArrowRight className="w-4 h-4" />
                    חזרה לניהול
                  </Button>
                  <h1 className="text-xl font-bold">הוספת מוצר חדש</h1>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Basic Information Card */}
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle>פרטי מוצר בסיסיים</CardTitle>
                        <div className="flex items-center space-x-2 space-x-reverse">
                          <Checkbox
                            id="is_active"
                            checked={formData.is_active}
                            onCheckedChange={(checked) => setFormData({ ...formData, is_active: !!checked })}
                          />
                          <Label
                            htmlFor="is_active"
                            className={`font-medium ${formData.is_active ? 'text-green-600' : 'text-red-600'}`}
                          >
                            {formData.is_active ? 'מוצר פעיל' : 'מוצר לא פעיל'}
                          </Label>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="name">שם המוצר *</Label>
                          <Input
                            id="name"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            placeholder="הזן שם המוצר"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="supplier">ספק</Label>
                          <div className="flex gap-2">
                            <Select
                              value={formData.supplier_id || ''}
                              onValueChange={(value) => setFormData({ ...formData, supplier_id: value })}
                            >
                              <SelectTrigger className="flex-1">
                                <SelectValue placeholder="בחר ספק" />
                              </SelectTrigger>
                              <SelectContent className="bg-background">
                                {suppliers.map((supplier: any) => (
                                  <SelectItem key={supplier.id} value={supplier.id}>
                                    {supplier.name}
                                    {supplier.city && ` (${supplier.city})`}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <SupplierQuickCreate onSupplierCreated={(id) => {
                              fetchSuppliers();
                              setFormData({ ...formData, supplier_id: id });
                            }} />
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="sku">מק"ט (לשימוש פנימי)</Label>
                          <Input
                            id="sku"
                            value={formData.sku || ''}
                            onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                            placeholder="הזן מק״ט"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="description">תיאור המוצר</Label>
                        <Textarea
                          id="description"
                          value={formData.description}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          placeholder="הזן תיאור מפורט של המוצר"
                          rows={3}
                        />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Categories Card */}
                  <Card>
                    <CardHeader>
                      <CardTitle>קטגוריות</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="category">קטגוריה *</Label>
                          <Select
                            value={formData.category_id}
                            onValueChange={(value) => {
                              setFormData({ ...formData, category_id: value, subcategory_id: '' });
                              setSubcategories([]);
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="בחר קטגוריה" />
                            </SelectTrigger>
                            <SelectContent className="bg-background">
                              {categories.map((category) => (
                                <SelectItem key={category.id} value={category.id}>
                                  {category.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="subcategory">תת-קטגוריה *</Label>
                          <Select
                            value={formData.subcategory_id}
                            onValueChange={(value) => setFormData({ ...formData, subcategory_id: value })}
                            disabled={!formData.category_id}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="בחר תת-קטגוריה" />
                            </SelectTrigger>
                            <SelectContent className="bg-background">
                              {subcategories.map((subcategory) => (
                                <SelectItem key={subcategory.id} value={subcategory.id}>
                                  {subcategory.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="grade">דירוג מוצר *</Label>
                        <Select
                          value={formData.grade}
                          onValueChange={(value: 'A' | 'B' | 'C' | 'D') => setFormData({ ...formData, grade: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="בחר דירוג" />
                          </SelectTrigger>
                          <SelectContent className="bg-background">
                            <SelectItem value="A">דירוג A - פרימיום (מצב מושלם, החזרה תוך 14 יום)</SelectItem>
                            <SelectItem value="B">דירוג B - סטנדרט (מצב טוב, החזרה תוך 7 ימים)</SelectItem>
                            <SelectItem value="C">דירוג C - כפי שהוא (ללא החזרה, אלא פגם מהותי)</SelectItem>
                            <SelectItem value="D">דירוג D - מכירה סופית (ללא החזרה בכל מקרה)</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground mt-1">
                          דירוג המוצר קובע את מדיניות ההחזרות והאחריות
                        </p>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Pricing and Inventory Card */}
                  <Card>
                    <CardHeader>
                      <CardTitle>מחיר ומלאי</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <Label htmlFor="price">מחיר ליח' (אחרי הנחה) *</Label>
                          <Input
                            id="price"
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.price_per_unit}
                            onChange={(e) => setFormData({ ...formData, price_per_unit: parseFloat(e.target.value) || 0 })}
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="cost_price">מחיר עלות (פנימי)</Label>
                          <Input
                            id="cost_price"
                            type="number"
                            step="0.01"
                            value={formData.cost_price || ''}
                            onChange={(e) => setFormData({ ...formData, cost_price: e.target.value ? parseFloat(e.target.value) : undefined })}
                            placeholder="0"
                            className="bg-muted/30"
                          />
                          {formData.price_per_unit > 0 && formData.cost_price && (
                            <div className={`text-xs mt-1 p-2 rounded ${((formData.price_per_unit - formData.cost_price) / formData.price_per_unit) * 100 < 20 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                              }`}>
                              <div className="flex justify-between font-medium">
                                <span>רווח: ₪{(formData.price_per_unit - formData.cost_price).toFixed(2)}</span>
                                <span>{(((formData.price_per_unit - formData.cost_price) / formData.price_per_unit) * 100).toFixed(1)}%</span>
                              </div>
                            </div>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="original_price">מחיר מקורי (לפני הנחה)</Label>
                          <Input
                            id="original_price"
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.original_price || ''}
                            onChange={(e) => setFormData({ ...formData, original_price: e.target.value ? parseFloat(e.target.value) : null })}
                            placeholder="אופציונלי"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            השאר ריק אם אין הנחה. המחיר המקורי חייב להיות גבוה מהמחיר הנוכחי.
                          </p>
                        </div>
                        <div>
                          <Label htmlFor="measurement_unit">יחידת מדידה *</Label>
                          <Select
                            value={formData.measurement_unit}
                            onValueChange={(value) => setFormData({ ...formData, measurement_unit: value })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-background">
                              <SelectItem value="unit">יח'</SelectItem>
                              <SelectItem value="square_meter">מ"ר</SelectItem>
                              <SelectItem value="liter">ליטר</SelectItem>
                              <SelectItem value="kilogram">ק"ג</SelectItem>
                              <SelectItem value="meter">מטר</SelectItem>
                              <SelectItem value="surface">משטח</SelectItem>
                              <SelectItem value="cubic_meter">מ"ק</SelectItem>
                              <SelectItem value="package_by_meter">חבילות (נמכר לפי מטר)</SelectItem>
                              <SelectItem value="package_by_square_meter">חבילות (נמכר לפי מ"ר)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {!formData.group_buy_enabled && (
                        <div>
                          <Label htmlFor="inventory">מלאי כולל *</Label>
                          <Input
                            id="inventory"
                            type="number"
                            min="0"
                            value={formData.total_inventory}
                            onChange={(e) => setFormData({ ...formData, total_inventory: parseInt(e.target.value) || 0 })}
                            required
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            המלאי הכולל המוצר - לא רלוונטי לקבוצות רכישה
                          </p>
                        </div>
                      )}
                      {formData.group_buy_enabled && (
                        <div className="p-4 bg-muted/50 rounded-lg">
                          <p className="text-sm text-muted-foreground">
                            💡 בקבוצת רכישה אין הגבלת מלאי - מקבלים הזמנות עד שמגיעים ליעד הכמותי
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>


                  {/* Quantity Settings Card */}
                  <Card>
                    <CardHeader>
                      <CardTitle>הגדרות כמות</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <Label htmlFor="min_quantity">
                            כמות מינימלית *
                            {(formData.measurement_unit === 'package_by_square_meter' || formData.measurement_unit === 'package_by_meter') && (
                              <span className="text-muted-foreground font-normal">
                                {' '}({formData.measurement_unit === 'package_by_square_meter' ? 'מ"ר' : 'מטר'})
                              </span>
                            )}
                          </Label>
                          <Input
                            id="min_quantity"
                            type="number"
                            min="1"
                            value={formData.min_quantity}
                            onChange={(e) => setFormData({ ...formData, min_quantity: parseInt(e.target.value) || 1 })}
                            required
                          />
                          {(formData.measurement_unit === 'package_by_square_meter' || formData.measurement_unit === 'package_by_meter') && (
                            <p className="text-xs text-muted-foreground mt-1">
                              מינימום ב{formData.measurement_unit === 'package_by_square_meter' ? 'מ"ר' : 'מטר'} שהלקוח יכול להזמין
                            </p>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="max_quantity">
                            כמות מקסימלית
                            {(formData.measurement_unit === 'package_by_square_meter' || formData.measurement_unit === 'package_by_meter') && (
                              <span className="text-muted-foreground font-normal">
                                {' '}({formData.measurement_unit === 'package_by_square_meter' ? 'מ"ר' : 'מטר'})
                              </span>
                            )}
                          </Label>
                          <Input
                            id="max_quantity"
                            type="number"
                            min="1"
                            value={formData.max_quantity || ''}
                            onChange={(e) => setFormData({ ...formData, max_quantity: e.target.value ? parseInt(e.target.value) : null })}
                            placeholder="ללא הגבלה"
                          />
                        </div>
                        <div>
                          <Label htmlFor="step_quantity">קפיצת כמות</Label>
                          <Input
                            id="step_quantity"
                            type="number"
                            min="1"
                            value={formData.step_quantity}
                            onChange={(e) => setFormData({ ...formData, step_quantity: parseInt(e.target.value) || 1 })}
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            {(formData.measurement_unit === 'package_by_square_meter' || formData.measurement_unit === 'package_by_meter')
                              ? `בכמה ${formData.measurement_unit === 'package_by_square_meter' ? 'מ"ר' : 'מטר'} הלקוח יכול לשנות (מומלץ: גודל חבילה)`
                              : 'בכמה יחידות הלקוח יכול לשנות את הכמות'}
                          </p>
                        </div>
                      </div>

                      {/* Package-specific settings - shown inline when package type is selected */}
                      {(formData.measurement_unit === 'package_by_square_meter' || formData.measurement_unit === 'package_by_meter') && (
                        <div className="p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg space-y-4">
                          <h4 className="font-semibold text-amber-800 dark:text-amber-200">⚙️ הגדרות חבילה (חובה)</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="package_coverage">
                                כמה {formData.measurement_unit === 'package_by_square_meter' ? 'מ"ר' : 'מטר'} בכל חבילה *
                              </Label>
                              <Input
                                id="package_coverage"
                                type="number"
                                step="0.01"
                                min="0.01"
                                value={formData.package_coverage_per_unit || ''}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setFormData({ ...formData, package_coverage_per_unit: value ? parseFloat(value) : undefined });
                                }}
                                placeholder="לדוגמה: 2.7"
                                className={!formData.package_coverage_per_unit ? 'border-amber-500' : ''}
                              />
                              {formData.package_coverage_per_unit && formData.price_per_unit && (
                                <p className="text-xs text-lime font-semibold mt-1">
                                  מחיר חבילה: ₪{(formData.price_per_unit * formData.package_coverage_per_unit).toFixed(2)}
                                </p>
                              )}
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            💡 הכמות המינימלית למעלה ({formData.min_quantity}) היא ב{formData.measurement_unit === 'package_by_square_meter' ? 'מ"ר' : 'מטר'}. המערכת תחשב אוטומטית כמה חבילות זה.
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>מידות ומאפיינים</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 gap-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="flex gap-2 items-end">
                            <div className="flex-1">
                              <Label htmlFor="dimension_width">רוחב</Label>
                              <Input
                                id="dimension_width"
                                type="number"
                                step="0.01"
                                value={formData.dimension_width || ''}
                                onChange={(e) => setFormData({ ...formData, dimension_width: e.target.value ? parseFloat(e.target.value) : null })}
                                placeholder="0"
                              />
                            </div>
                            <div className="w-32">
                              <Select
                                value={formData.dimension_width_unit || 'cm'}
                                onValueChange={(value) => setFormData({ ...formData, dimension_width_unit: value })}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="יח'" />
                                </SelectTrigger>
                                <SelectContent className="bg-background">
                                  <SelectItem value="cm">ס"מ</SelectItem>
                                  <SelectItem value="mm">מ"מ</SelectItem>
                                  <SelectItem value="m">מטר</SelectItem>
                                  <SelectItem value="inch">אינץ'</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          <div className="flex gap-2 items-end">
                            <div className="flex-1">
                              <Label htmlFor="dimension_height">אורך</Label>
                              <Input
                                id="dimension_height"
                                type="number"
                                step="0.01"
                                value={formData.dimension_height || ''}
                                onChange={(e) => setFormData({ ...formData, dimension_height: e.target.value ? parseFloat(e.target.value) : null })}
                                placeholder="0"
                              />
                            </div>
                            <div className="w-32">
                              <Select
                                value={formData.dimension_height_unit || 'cm'}
                                onValueChange={(value) => setFormData({ ...formData, dimension_height_unit: value })}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="יח'" />
                                </SelectTrigger>
                                <SelectContent className="bg-background">
                                  <SelectItem value="cm">ס"מ</SelectItem>
                                  <SelectItem value="mm">מ"מ</SelectItem>
                                  <SelectItem value="m">מטר</SelectItem>
                                  <SelectItem value="inch">אינץ'</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="flex gap-2 items-end">
                            <div className="flex-1">
                              <Label htmlFor="dimension_depth">עובי</Label>
                              <Input
                                id="dimension_depth"
                                type="number"
                                step="0.01"
                                value={formData.dimension_depth || ''}
                                onChange={(e) => setFormData({ ...formData, dimension_depth: e.target.value ? parseFloat(e.target.value) : null })}
                                placeholder="0"
                              />
                            </div>
                            <div className="w-32">
                              <Select
                                value={formData.dimension_depth_unit || 'mm'}
                                onValueChange={(value) => setFormData({ ...formData, dimension_depth_unit: value })}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="יח'" />
                                </SelectTrigger>
                                <SelectContent className="bg-background">
                                  <SelectItem value="cm">ס"מ</SelectItem>
                                  <SelectItem value="mm">מ"מ</SelectItem>
                                  <SelectItem value="m">מטר</SelectItem>
                                  <SelectItem value="inch">אינץ'</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t pt-4">
                        <div>
                          <Label htmlFor="country_of_origin">ארץ ייצור</Label>
                          <Input
                            id="country_of_origin"
                            value={formData.country_of_origin || ''}
                            onChange={(e) => setFormData({ ...formData, country_of_origin: e.target.value })}
                            placeholder="לדוגמה: ספרד, איטליה, סין"
                          />
                        </div>
                        <div>
                          <Label htmlFor="material">חומר</Label>
                          <Input
                            id="material"
                            value={formData.material || ''}
                            onChange={(e) => setFormData({ ...formData, material: e.target.value })}
                            placeholder="לדוגמה: פורצלן, קרמיקה, עץ"
                          />
                        </div>
                        <div>
                          <Label htmlFor="finish">גימור</Label>
                          <Input
                            id="finish"
                            value={formData.finish || ''}
                            onChange={(e) => setFormData({ ...formData, finish: e.target.value })}
                            placeholder="לדוגמה: מט, מבריק, לפאטו"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>


                  {/* Unified Sale Type Section */}
                  <Card>
                    <CardHeader>
                      <CardTitle>סוג מכירה</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Toggle between Regular and Group Buy */}
                      <div>
                        <Label htmlFor="sale-mode">בחר סוג מכירה</Label>
                        <Select
                          value={
                            formData.group_buy_enabled
                              ? "group_buy"
                              : formData.sale_type
                          }
                          onValueChange={(value) => {
                            if (value === "group_buy") {
                              setFormData({
                                ...formData,
                                group_buy_enabled: true,
                                sale_type: 'immediate',
                                total_inventory: 0 // Group buy products don't use inventory limits
                              });
                            } else if (value === "liquidation") {
                              setFormData({
                                ...formData,
                                group_buy_enabled: false,
                                sale_type: 'liquidation',
                                total_inventory: 0
                              });
                            } else {
                              setFormData({
                                ...formData,
                                group_buy_enabled: false,
                                sale_type: 'immediate',
                                total_inventory: 0
                              });
                            }
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-background">
                            <SelectItem value="regular">מכירה רגילה</SelectItem>
                            <SelectItem value="liquidation">מכירת חיסול</SelectItem>
                            <SelectItem value="group_buy">קבוצת רכישה</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Regular Sale Options */}
                      {!formData.group_buy_enabled && (
                        <>
                          <div className="flex items-center space-x-2 space-x-reverse">
                            <Checkbox
                              id="is_timer_sale"
                              checked={formData.sale_type === 'timer'}
                              onCheckedChange={(checked) => setFormData({ ...formData, sale_type: checked ? 'timer' : 'immediate' })}
                            />
                            <Label htmlFor="is_timer_sale" className="cursor-pointer">
                              מבצע מוגבל בזמן
                            </Label>
                          </div>

                          {formData.sale_type === 'timer' && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
                              <div>
                                <Label htmlFor="sale_start">תחילת מבצע</Label>
                                <Input
                                  id="sale_start"
                                  type="datetime-local"
                                  value={formData.sale_start || ''}
                                  onChange={(e) => setFormData({ ...formData, sale_start: e.target.value || null })}
                                />
                              </div>
                              <div>
                                <Label htmlFor="sale_end">סיום מבצע *</Label>
                                <Input
                                  id="sale_end"
                                  type="datetime-local"
                                  value={formData.sale_end || ''}
                                  onChange={(e) => setFormData({ ...formData, sale_end: e.target.value || null })}
                                  required={formData.sale_type === 'timer'}
                                />
                                <p className="text-xs text-muted-foreground mt-1">
                                  תאריך וזמן סיום המבצע חובה עבור מבצע מוגבל בזמן
                                </p>
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      {/* Group Buy Options */}
                      {formData.group_buy_enabled && (
                        <SimpleGroupBuyFields
                          isGroupBuy={formData.group_buy_enabled}
                          targetQuantity={formData.group_buy_target_quantity || null}
                          endDate={formData.group_buy_end_date}
                          lockedPrice={formData.group_buy_locked_price || null}
                          basePrice={formData.price_per_unit}
                          measurementUnit={formData.measurement_unit || 'unit'}
                          limitToTarget={formData.group_buy_limit_to_target || false}
                          onIsGroupBuyChange={(enabled) => setFormData({ ...formData, group_buy_enabled: enabled })}
                          onTargetQuantityChange={(value) =>
                            setFormData({ ...formData, group_buy_target_quantity: value })
                          }
                          onEndDateChange={(value) =>
                            setFormData({ ...formData, group_buy_end_date: value })
                          }
                          onLockedPriceChange={(value) =>
                            setFormData({ ...formData, group_buy_locked_price: value })
                          }
                          onLimitToTargetChange={(value) =>
                            setFormData({ ...formData, group_buy_limit_to_target: value })
                          }
                        />
                      )}

                      {/* Liquidation Sale Options */}
                      {formData.sale_type === 'liquidation' && (
                        <LiquidationSaleFields
                          isLiquidation={formData.sale_type === 'liquidation'}
                          totalInventory={formData.total_inventory}
                          thresholdPercent={formData.liquidation_threshold_percent || null}
                          deliveryDays={formData.liquidation_delivery_days || null}
                          measurementUnit={formData.measurement_unit || 'unit'}
                          onIsLiquidationChange={() => { }} // Controlled by select above
                          onTotalInventoryChange={() => { }} // Handled by main inventory field
                          onThresholdPercentChange={(value) =>
                            setFormData({ ...formData, liquidation_threshold_percent: value })
                          }
                          onDeliveryDaysChange={(value) =>
                            setFormData({ ...formData, liquidation_delivery_days: value })
                          }
                        />
                      )}
                    </CardContent>
                  </Card>

                  {/* Product Images Card */}
                  <Card>
                    <CardHeader>
                      <CardTitle>תמונות מוצר</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ProductImageGallery
                        images={formData.images}
                        onImagesChange={(images) => setFormData({ ...formData, images })}
                        productTitle={formData.name}
                      />
                    </CardContent>
                  </Card>

                  {/* Submit Buttons */}
                  <div className="flex gap-4">
                    <Button type="submit" disabled={loading} className="flex items-center gap-2">
                      <Save className="w-4 h-4" />
                      {loading ? 'שומר...' : 'הוסף מוצר'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate('/admin?tab=products')}
                    >
                      ביטול
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </SidebarInset>
        </div>
      </SidebarProvider>
    </div>
  );
};

export default AdminProductCreate;
